// SwipeableRow
//
// Wraps a single list row so that the user can swipe left to reveal one or
// two action buttons (delete / archive / etc.). Touch-only behaviour —
// safe to ignore on desktop where ctrl+click and explicit row actions
// already exist.
//
// Usage:
//
//   <SwipeableRow
//     rightActions={[
//       { label: 'Archive', color: 'amber', icon: Archive, onAction: () => archive() },
//       { label: 'Delete',  color: 'red',   icon: Trash2,  onAction: () => del()    },
//     ]}
//   >
//     <ListContent />
//   </SwipeableRow>
//
// Design choices:
//   * Pure CSS transforms (no animation library) — drops cleanly on a slow
//     low-end Android device.
//   * Snaps to either fully-closed or fully-open positions; no in-between.
//   * Tapping the row content while open closes it instead of triggering
//     the row's primary click — prevents accidental navigation while
//     trying to swipe.
//   * Each action button is the full row height for an easy tap target.

import React, { useRef, useState, useEffect, useCallback } from 'react';

const ACTION_WIDTH = 80; // px per action button
const SWIPE_THRESHOLD = 30; // px swipe distance to begin tracking
const OPEN_THRESHOLD = 0.4; // open if swiped ≥ 40% of total action width

const COLORS = {
  red:    'bg-red-600 text-white hover:bg-red-700',
  amber:  'bg-amber-500 text-white hover:bg-amber-600',
  indigo: 'bg-indigo-600 text-white hover:bg-indigo-700',
  slate:  'bg-slate-600 text-white hover:bg-slate-700',
  green:  'bg-emerald-600 text-white hover:bg-emerald-700',
};

export default function SwipeableRow({ children, rightActions = [], onTap, className = '' }) {
  const actionsWidth = rightActions.length * ACTION_WIDTH;
  const containerRef = useRef(null);

  // translateX is negative when revealing right actions.
  const [translateX, setTranslateX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const startTranslate = useRef(0);
  const moved = useRef(false);

  // Close when clicking outside.
  useEffect(() => {
    if (!isOpen) return;
    const onAnyClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onAnyClick);
    document.addEventListener('touchstart', onAnyClick);
    return () => {
      document.removeEventListener('mousedown', onAnyClick);
      document.removeEventListener('touchstart', onAnyClick);
    };
    // `close` is a useCallback with empty deps so it's stable across
    // renders — including it in the dep array would just cause noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const close = useCallback(() => {
    setTranslateX(0);
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setTranslateX(-actionsWidth);
    setIsOpen(true);
  }, [actionsWidth]);

  // ── Touch handlers ───────────────────────────────────────────────────
  const onTouchStart = (e) => {
    if (rightActions.length === 0) return;
    startX.current = e.touches[0].clientX;
    startTranslate.current = translateX;
    moved.current = false;
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!isDragging) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) moved.current = true;
    // Negative dx = swipe left (revealing right actions). Clamp to range.
    const next = Math.max(-actionsWidth, Math.min(0, startTranslate.current + dx));
    setTranslateX(next);
  };

  const onTouchEnd = () => {
    setIsDragging(false);
    if (Math.abs(translateX) > actionsWidth * OPEN_THRESHOLD) {
      open();
    } else {
      close();
    }
  };

  // Tap inside content closes if open, otherwise propagates to onTap.
  const onContentClick = (e) => {
    if (isOpen) { e.stopPropagation(); close(); return; }
    if (moved.current) { e.stopPropagation(); return; }
    onTap?.(e);
  };

  return (
    <div
      ref={containerRef}
      className={['relative overflow-hidden', className].join(' ')}
    >
      {/* Action layer — sits behind the content */}
      {rightActions.length > 0 && (
        <div
          className="absolute right-0 top-0 bottom-0 flex"
          style={{ width: actionsWidth }}
        >
          {rightActions.map((a, i) => {
            const Icon = a.icon;
            return (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); close(); a.onAction?.(); }}
                className={[
                  'flex flex-col items-center justify-center gap-1 text-xs font-medium transition',
                  COLORS[a.color] || COLORS.slate,
                ].join(' ')}
                style={{ width: ACTION_WIDTH }}
                aria-label={a.label}
              >
                {Icon && <Icon size={16} />}
                <span>{a.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Sliding content */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={onContentClick}
        className="relative bg-white dark:bg-slate-900"
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out',
          touchAction: 'pan-y',
        }}
      >
        {children}
      </div>
    </div>
  );
}
